import { Text, Grid, GridItem, Skeleton, Button, Icon } from '@chakra-ui/react';
import { useResource, useResourceRelationship } from 'datx-jsonapi-react';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import LeftIcon from '@/assets/icons/ic-left.svg';

import { TimeZoneLabel } from '@/components/event/TimeZoneLabel/TimeZoneLabel';
import { ScheduleSearchInput } from '@/components/ui/features/schedule/ScheduleSearchInput/ScheduleSearchInput';
import { ScheduleTrackFilter } from '@/components/ui/features/schedule/ScheduleTrackFilter/ScheduleTrackFilter';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryThinEvent, queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { EmbeddingScheduleType, EventConfiguration } from '@/resources/EventConfiguration';

interface IUrlQueryStringParams {
  query?: string;
  eventId?: string;
}

export const ScheduleHeaderEmbedded: FC = () => {
  const { query, eventId, change } = useQueryParams<IUrlQueryStringParams>({
    query: StringParam,
    eventId: StringParam,
    timeZone: StringParam,
  });
  const { data: event } = useResource(queryThinEvent(eventId));
  const { t } = useTranslation();

  const { data: eventConfiguration } = useResourceRelationship<EventConfiguration>(
    queryThinEventConfigurationRelationship(eventId)
  );

  const isSimplified = eventConfiguration.embeddingScheduleType === EmbeddingScheduleType.SIMPLIFIED;

  const onBackClick = useCallback(() => {
    change({ query: null }, { method: 'replace', shallow: true });
  }, [change]);

  return (
    <Grid gridAutoFlow={{ base: 'row', md: query ? 'column' : 'row', xl: 'column' }} borderBottom="1px solid #CCCCCC">
      <Grid p={6} fontSize="14px" justifyContent={'flex-start'}>
        <GridItem>
          <Skeleton isLoaded={Boolean(event)} display={{ base: 'block', md: 'inline-block' }}>
            <Text lineHeight="18px" casing="uppercase" letterSpacing="0.05em">
              {event?.name} / {t('eventResource.schedule')}
            </Text>
          </Skeleton>
        </GridItem>
        <GridItem>
          <Skeleton isLoaded={Boolean(event)} display={{ base: 'block', md: 'inline-block' }} mt={2}>
            <TimeZoneLabel />
          </Skeleton>
        </GridItem>
      </Grid>
      {!isSimplified && (
        <Grid
          p={6}
          gap={'4'}
          alignItems="center"
          gridAutoFlow={{ base: 'row', lg: 'column' }}
          justifyContent={{ base: 'center', lg: 'flex-end' }}
        >
          {!query && (
            <GridItem>
              <ScheduleTrackFilter />
            </GridItem>
          )}
          {query && (
            <GridItem>
              <Button
                onClick={onBackClick}
                size="sm"
                p={2}
                leftIcon={<Icon color="white" w={8} h={8} as={LeftIcon} />}
              />
            </GridItem>
          )}
          <GridItem>
            <ScheduleSearchInput minW={['300px', '360px']} maxW="500px" />
          </GridItem>
        </Grid>
      )}
    </Grid>
  );
};
